import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import onsiteproLogo from "../../assets/logo2 2.png";
import AdminTaylorImportModal, { adminIsPrimaryAdmin } from "./AdminTaylorImportModal";
import { ensureFormUuid } from "../../utils/formUuid";
import { buildSharePointFileName, toDdMmYyyy, addedByName } from "../../utils/formPdfHelpers";

import { canEditDeleteRecords } from "../../utils/rolePermissions";
const THEME = "#8b1634";

function getBaseUrl() {
  const apiUrl = process.env.REACT_APP_API_URL;
  return apiUrl?.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl || "";
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function parseStoredDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const s = String(value).trim();
  const dmy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmy) {
    const day = parseInt(dmy[1], 10);
    const month = parseInt(dmy[2], 10) - 1;
    const year = parseInt(dmy[3], 10);
    const d = new Date(year, month, day);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) {
    const year = parseInt(ymd[1], 10);
    const month = parseInt(ymd[2], 10) - 1;
    const day = parseInt(ymd[3], 10);
    const d = new Date(year, month, day);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

function dateToYmd(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function normalizeImageSource(value, fallbackMime = "image/png") {
  if (!value) return "";
  let src = value;
  if (Array.isArray(src)) src = src[0];
  if (src == null) return "";
  if (typeof src !== "string") src = String(src);
  src = src.trim();
  if (!src) return "";
  if (src.startsWith("data:image")) return src;
  const compact = src.replace(/\s+/g, "").replace(/,+$/, "");
  if (!compact) return "";
  return `data:${fallbackMime};base64,${compact}`;
}

function getPdfImageType(imageSrc, fallbackType = "PNG") {
  if (!imageSrc || typeof imageSrc !== "string") return fallbackType;
  const m = imageSrc.match(/^data:image\/([a-zA-Z0-9+.-]+);base64,/i);
  const subtype = m?.[1]?.toLowerCase();
  if (subtype === "png") return "PNG";
  if (subtype === "jpg" || subtype === "jpeg") return "JPEG";
  if (subtype === "webp") return "WEBP";
  return fallbackType;
}

function normalizeWitnessRecordShape(record) {
  if (!record || typeof record !== "object") return record;
  return {
    ...record,
    incidentDate: record.incidentDate || record.dateOfIncident || "",
    incidentTime: record.incidentTime || record.timeOfIncident || "",
    declarationSignature: record.declarationSignature || record.signature || "",
    declarationDate: record.declarationDate || record.signatureDate || "",
  };
}

const FIELD_STYLE = {
  border: "1px solid #cbd5e1",
  fontSize: 12,
  padding: "8px 10px",
  verticalAlign: "middle",
  background: "#fff"
};

const LABEL_STYLE = {
  ...FIELD_STYLE,
  width: 190,
  fontWeight: 700,
  color: THEME,
  background: "#f8f1f4"
};

export default function WitnessStatementPage() {
  const canEditDelete = canEditDeleteRecords();
  const [records, setRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(true);
  const [appUsers, setAppUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const isAdminUser = adminIsPrimaryAdmin();
  const [editForm, setEditForm] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [uploadingKey, setUploadingKey] = useState(null);
  const [showSharePointUploadModal, setShowSharePointUploadModal] = useState(false);
  const [sharePointUploadRecord, setSharePointUploadRecord] = useState(null);
  const [sharePointPhaseType, setSharePointPhaseType] = useState("phase");
  const [sharePointPhaseNumber, setSharePointPhaseNumber] = useState("15");
  const [sharePointNetwork, setSharePointNetwork] = useState("");
  const [pendingDirectUpload, setPendingDirectUpload] = useState(false);
  useEffect(() => {
    if (pendingDirectUpload && sharePointUploadRecord) {
      setPendingDirectUpload(false);
      // eslint-disable-next-line no-use-before-define
      handleSharePointUpload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDirectUpload, sharePointUploadRecord]);

  useEffect(() => {
    fetchRecords();
    fetchAppUsers();
  }, []);

  async function fetchRecords() {
    setLoadingRecords(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/witness-statement", {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setRecords(
        res.ok && data.success && Array.isArray(data.data)
          ? data.data.map((r) => normalizeWitnessRecordShape(r))
          : []
      );
    } catch {
      setRecords([]);
    } finally {
      setLoadingRecords(false);
    }
  }

  async function fetchAppUsers() {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/app-users", {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setAppUsers(res.ok && data.success && Array.isArray(data.data) ? data.data : []);
    } catch {
      setAppUsers([]);
    }
  }

  const getUserName = (record) => {
    const uid = record?.userId;
    const u = appUsers.find((x) => x._id === uid);
    if (u) {
      const full = `${u.firstName || ""} ${u.lastName || ""}`.trim();
      return full || "—";
    }
    const named = `${record?.addedByFirstName || ""} ${record?.addedByLastName || ""}`.trim();
    return named || "—";
  };

  const filtered = records.filter((r) => {
    const fields = [r.name, r.jobTitle, r.incidentDate, r.siteAddressLocation, r.projectNumber, r.whatDidYouSee].map((v) => (v || "").toLowerCase());
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return words.length === 0 || words.every((w) => fields.some((field) => field.includes(w)));
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice().slice((page - 1) * pageSize, page * pageSize);

  const handleEdit = (r) => {
    const normalized = normalizeWitnessRecordShape(r);
    setEditForm({ ...normalized, formUuid: ensureFormUuid(normalized?.formUuid) });
    setShowEditModal(true);
  };

  const handleEditSave = async () => {
    if (!editForm?._id) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/witness-statement/" + editForm._id, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: editForm.name,
          jobTitle: editForm.jobTitle,
          homeAddress: editForm.homeAddress,
          contactTelephoneNumber: editForm.contactTelephoneNumber,
          incidentDate: editForm.incidentDate,
          incidentTime: editForm.incidentTime,
          siteAddressLocation: editForm.siteAddressLocation,
          projectNumber: editForm.projectNumber,
          exactLocationOfIncident: editForm.exactLocationOfIncident,
          whatDidYouSee: editForm.whatDidYouSee,
          declarationSignature: editForm.declarationSignature,
          declarationDate: editForm.declarationDate,
          formUuid: ensureFormUuid(editForm.formUuid)
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await fetchRecords();
        setShowEditModal(false);
        setEditForm(null);
        toast.success("Record updated!");
      } else {
        toast.error(data.message || "Failed to update");
      }
    } catch {
      toast.error("Failed to update");
    }
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) {
      toast.error("Invalid record id");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/witness-statement/" + deleteId, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setRecords(records.filter((f) => f._id !== deleteId));
        toast.success("Record deleted!");
      } else {
        toast.error(data.message || "Failed to delete");
      }
    } catch {
      toast.error("Failed to delete");
    }
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const handleSharePointUpload = async () => {
    const nameForUpload = (sharePointUploadRecord?.name || "").trim();
    if (!nameForUpload) {
      toast.error("Name is missing for this record.");
      return;
    }
    if (sharePointPhaseType === "phase" && (!sharePointNetwork || sharePointNetwork.trim() === "")) {
      toast.error("Please select Area.");
      return;
    }
    const payloadPhaseType = sharePointPhaseType === "phase" ? "phase" : "non-phase";
    const payloadNetwork = sharePointPhaseType === "phase" ? sharePointNetwork : "";
    const payloadPhaseNumber = sharePointPhaseType === "phase" ? sharePointPhaseNumber : "";
    setShowSharePointUploadModal(false);
    await generatePDF(sharePointUploadRecord, {
      uploadOnly: true,
      phaseType: payloadPhaseType,
      phaseNumber: payloadPhaseNumber,
      network: payloadNetwork,
      siteName: nameForUpload,
      onSuccess: () => {
        setSharePointUploadRecord(null);
        setSharePointPhaseType("phase");
        setSharePointPhaseNumber("15");
        setSharePointNetwork("");
      },
      onError: () => {
        setPendingDirectUpload(false);
      }
    });
  };

  const generatePDF = async (record, options = {}) => {
    const shouldUploadToSharePoint = options.uploadOnly === true;
    const uploadConfig = shouldUploadToSharePoint
      ? {
          phaseType: options.phaseType || "non-phase",
          phaseNumber: options.phaseNumber || "",
          network: options.network || "",
          siteName: options.siteName || "",
          onSuccess: options.onSuccess,
          onError: options.onError
        }
      : null;
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const primaryColor = [139, 22, 52];
    const headerHeight = 30;

    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, pageWidth, headerHeight, "F");
    try {
      const logoImg = new Image();
      await new Promise((resolve) => {
        logoImg.onload = resolve;
        logoImg.onerror = resolve;
        logoImg.src = onsiteproLogo;
      });
      doc.addImage(logoImg, "PNG", 10, 5, 65, 20);
    } catch {}

    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("SF-003-B Witness Statement", 90, 13);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text("TAYLOR TECHNOLOGY SYSTEMS LIMITED", 90, 19);

    let y = headerHeight + 8;
    const common = {
      styles: { font: "helvetica", fontSize: 9, cellPadding: 3, overflow: "linebreak", textColor: [0, 0, 0] },
      theme: "grid",
      headStyles: { fillColor: [245, 245, 245], textColor: [0, 0, 0], lineWidth: 0.2 },
      bodyStyles: { textColor: [0, 0, 0], lineWidth: 0.2 },
      margin: { left: 10, right: 10 }
    };

    doc.setFillColor(...primaryColor);
    doc.rect(10, y, pageWidth - 20, 8, "F");
    doc.setTextColor(255, 255, 255);
    doc.text("1", 14, y + 5.5);
    doc.text("Details of person giving the statement", 22, y + 5.5);
    y += 8;

    autoTable(doc, {
      ...common,
      startY: y,
      columnStyles: { 0: { cellWidth: 48 }, 1: { cellWidth: 47 }, 2: { cellWidth: 48 }, 3: { cellWidth: 47 } },
      body: [
        [
          { content: "Name:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
          { content: record.name || "" },
          { content: "Job Title:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
          { content: record.jobTitle || "" }
        ],
        [{ content: "Home Address:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, { content: record.homeAddress || "", colSpan: 3 }],
        [{ content: "Contact Telephone Number:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, { content: record.contactTelephoneNumber || "" }, { content: "Added by:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, { content: getUserName(record) }]
      ]
    });
    y = doc.lastAutoTable.finalY + 2;

    doc.setFillColor(...primaryColor);
    doc.rect(10, y, pageWidth - 20, 8, "F");
    doc.setTextColor(255, 255, 255);
    doc.text("2", 14, y + 5.5);
    doc.text("Details of the Incident", 22, y + 5.5);
    y += 8;

    autoTable(doc, {
      ...common,
      startY: y,
      columnStyles: { 0: { cellWidth: 48 }, 1: { cellWidth: 47 }, 2: { cellWidth: 48 }, 3: { cellWidth: 47 } },
      body: [
        [
          { content: "Date of Incident:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
          { content: record.incidentDate || "" },
          { content: "Time of Incident:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
          { content: record.incidentTime || "" }
        ],
        [{ content: "Site Address / Location:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, { content: record.siteAddressLocation || "" }, { content: "Project Number:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, { content: record.projectNumber || "" }],
        [{ content: "Exact Location of the Incident:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } }, { content: record.exactLocationOfIncident || "", colSpan: 3 }]
      ]
    });
    y = doc.lastAutoTable.finalY + 2;

    doc.setFillColor(...primaryColor);
    doc.rect(10, y, pageWidth - 20, 8, "F");
    doc.setTextColor(255, 255, 255);
    doc.text("3", 14, y + 5.5);
    doc.text("What Did You See?", 22, y + 5.5);
    y += 8;
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(9);
    const seeLines = doc.splitTextToSize(record.whatDidYouSee || "—", pageWidth - 24);
    doc.text(seeLines, 12, y + 6);
    y += 6 + seeLines.length * 4.8;

    if (y > pageHeight - 80) {
      doc.addPage();
      y = 20;
    }

    doc.setFillColor(...primaryColor);
    doc.rect(10, y, pageWidth - 20, 8, "F");
    doc.setTextColor(255, 255, 255);
    doc.text("4", 14, y + 5.5);
    doc.text("Continuation Sheet", 22, y + 5.5);
    y += 8;
    doc.setTextColor(0, 0, 0);
    const contLines = doc.splitTextToSize(record.continuationSheet || "—", pageWidth - 24);
    doc.text(contLines, 12, y + 6);
    y += 6 + contLines.length * 4.8;

    if (y > pageHeight - 60) {
      doc.addPage();
      y = 20;
    }

    doc.setFillColor(...primaryColor);
    doc.rect(10, y, pageWidth - 20, 8, "F");
    doc.setTextColor(255, 255, 255);
    doc.text("5", 14, y + 5.5);
    doc.text("Declaration", 22, y + 5.5);
    y += 8;

    const signatureSrc = normalizeImageSource(record.declarationSignature, "image/png");
    autoTable(doc, {
      ...common,
      startY: y,
      columnStyles: { 0: { cellWidth: 48 }, 1: { cellWidth: 47, minCellHeight: 22 }, 2: { cellWidth: 48 }, 3: { cellWidth: 47 } },
      body: [
        [
          { content: "Signature:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
          { content: "" },
          { content: "Date:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
          { content: record.declarationDate || "" }
        ],
        [{ content: "I certify that the details completed within this document are correct to the best of my knowledge.", colSpan: 4 }]
      ],
      didDrawCell: (data) => {
        if (data.section !== "body" || data.row.index !== 0 || data.column.index !== 1) return;
        if (!signatureSrc) return;
        try {
          const cell = data.cell;
          const x = cell.x + 2;
          const y2 = cell.y + 2;
          const w = cell.width - 4;
          const h = cell.height - 4;
          doc.addImage(signatureSrc, getPdfImageType(signatureSrc, "PNG"), x, y2, w, h, "", "NONE");
        } catch {}
      }
    });

    const safeName = String(record.name || "witness-statement").replace(/[\\/:"*?<>|]+/g, "-");
    const safeDate = String(record.incidentDate || "date").replace(/[\\/:"*?<>|]+/g, "-");
    const fileName = shouldUploadToSharePoint
      ? buildSharePointFileName([record.name, record.siteAddressLocation, toDdMmYyyy(record.incidentDate)])
      : `SF003B_${safeName}_${safeDate}.pdf`;

    if (shouldUploadToSharePoint && uploadConfig) {
      try {
        const key = `${record._id || ""}|${record.name || ""}|${record.incidentDate || ""}`;
        setUploadingKey(key);
        const apiUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
        const dataUri = doc.output("datauristring");
        const uploadParams = {
          fileName,
          fileBase64: dataUri,
          phaseType: uploadConfig.phaseType,
          phaseNumber: uploadConfig.phaseNumber,
          network: uploadConfig.network,
          siteName: uploadConfig.siteName, uploadContext: "hseq-forms-tablets", uploadSubFolder: "SF-003-B Witness Statement"
        };
        const res = await fetch(`${apiUrl}/api/sharepoint/upload`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
          body: JSON.stringify(uploadParams)
        });
        if (res.status !== 204 && !res.ok) {
          let msg = "SharePoint upload failed";
          try {
            const out = await res.json();
            msg = out?.message || msg;
          } catch {}
          throw new Error(msg);
        }
        toast.success("PDF uploaded to SharePoint successfully!");
        if (uploadConfig.onSuccess) uploadConfig.onSuccess();
      } catch (err) {
        toast.error(err?.message || "SharePoint upload failed");
        if (uploadConfig.onError) uploadConfig.onError(err);
      } finally {
        setUploadingKey(null);
      }
    } else {
      doc.save(fileName);
      toast.success("SF-003-B Witness Statement PDF generated successfully!");
    }
  };

  return (
    <>
      <div style={{ background: "#fff", borderRadius: 20, boxShadow: "0px 4px 20px rgba(0,0,0,0.08)", padding: 24, marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 16 }}>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: "#1a202c", margin: 0 }}>SF-003-B Witness Statement</h2>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {isAdminUser && (
              <button
                type="button"
                onClick={() => setShowImportModal(true)}
                style={{
                  background: "#8b1634",
                  color: "#fff",
                  border: "none",
                  borderRadius: "10px",
                  padding: "10px 14px",
                  fontWeight: "600",
                  fontSize: "13px",
                  cursor: "pointer",
                }}
              >
                Import File
              </button>
            )}
          <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
            <i className="fa fa-search" style={{ position: "absolute", left: 12, color: THEME, fontSize: 14, pointerEvents: "none", zIndex: 1 }} />
            <input type="text" placeholder="Type to Search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} style={{ padding: "10px 12px 10px 36px", borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 14, outline: "none", width: 250 }} />
          </div>
          </div>
        </div>
        <div style={{ overflowX: "auto", borderRadius: 12, border: "1px solid #e2e8f0" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f7fafc", borderBottom: "1px solid #e2e8f0" }}>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Name</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Job Title</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Incident Date</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Project Number</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Added By</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Updated Date</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loadingRecords ? (
                <tr><td colSpan={7} style={{ textAlign: "center", padding: 40, color: THEME, fontSize: 16, fontWeight: 600 }}><i className="fa fa-spinner fa-spin" style={{ marginRight: 8 }} />Loading records...</td></tr>
              ) : paged.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: "center", padding: 40, color: "#718096", fontSize: 16 }}>No records found.</td></tr>
              ) : (
                paged.map((r, idx) => {
                  const updatedDate = r.updatedAt || r.createdAt;
                  const k = `${r._id || ""}|${r.name || ""}|${r.incidentDate || ""}`;
                  return (
                    <tr key={r._id || idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{r.name || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{r.jobTitle || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{r.incidentDate || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{r.projectNumber || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{getUserName(r)}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{formatDate(updatedDate)}</td>
                      <td style={{ padding: 16 }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          {canEditDelete && (
                            <button type="button" onClick={() => handleEdit(r)} title="Edit" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}><i className="fa fa-edit" style={{ color: "#22c55e", fontSize: 16 }} /></button>
                          )}
                          <button type="button" onClick={() => generatePDF(r)} title="Generate PDF" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}><i className="fa fa-file-pdf-o" style={{ color: "#dc2626", fontSize: 16 }} /></button>
                          <button
                            type="button"
                            onClick={() => {
                              if (!uploadingKey) {
                                setSharePointUploadRecord(r);
                                setSharePointPhaseType("non-phase"); setPendingDirectUpload(true);
                              }
                            }}
                            disabled={uploadingKey === k}
                            title={uploadingKey === k ? "Uploading..." : "Upload to SharePoint"}
                            style={{ background: "transparent", border: "none", cursor: uploadingKey === k ? "not-allowed" : "pointer", padding: 6, opacity: uploadingKey === k ? 0.6 : 1 }}
                          >
                            <i className={uploadingKey === k ? "fa fa-spinner fa-spin" : "fa fa-upload"} style={{ color: "#22c55e", fontSize: 16 }} />
                          </button>
                          {canEditDelete && (
                            <button type="button" onClick={() => handleDelete(r._id || r.id)} title="Delete" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}><i className="fa fa-trash" style={{ color: "#e53e3e", fontSize: 16 }} /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showSharePointUploadModal && sharePointUploadRecord && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ maxWidth: "600px", margin: "0 auto" }}>
              <div className="modal-header">
                <h5 className="modal-title">Upload to SharePoint</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowSharePointUploadModal(false);
                    setSharePointUploadRecord(null);
                    setSharePointPhaseType("phase");
                    setSharePointPhaseNumber("15");
                    setSharePointNetwork("");
                  }}
                />
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="mb-3 col-12">
                    <label className="form-label fw-semibold">Type <span style={{ color: "red" }}>*</span></label>
                    <select
                      className="form-select"
                      value={sharePointPhaseType}
                      onChange={(e) => {
                        setSharePointPhaseType(e.target.value);
                        if (e.target.value === "non-phase") setSharePointNetwork("");
                      }}
                    >
                      <option value="phase">Phase</option>
                      <option value="non-phase">Non Phase</option>
                    </select>
                  </div>
                  {sharePointPhaseType === "phase" && (
                    <>
                      <div className="mb-3 col-md-6">
                        <label className="form-label fw-semibold">Phase Number <span style={{ color: "red" }}>*</span></label>
                        <select className="form-select" value={sharePointPhaseNumber} onChange={(e) => setSharePointPhaseNumber(e.target.value)}>
                          <option value="15">Phase 15 (2025)</option>
                          <option value="16">Phase 16 (2026)</option>
                        </select>
                      </div>
                      <div className="mb-3 col-md-6">
                        <label className="form-label fw-semibold">Area <span style={{ color: "red" }}>*</span></label>
                        <select className="form-select" value={sharePointNetwork} onChange={(e) => setSharePointNetwork(e.target.value)}>
                          <option value="">Select Area</option>
                          <option value="EPN">EPN</option>
                          <option value="LPN">LPN</option>
                          <option value="SPN">SPN</option>
                        </select>
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowSharePointUploadModal(false);
                    setSharePointUploadRecord(null);
                    setSharePointPhaseType("phase");
                    setSharePointPhaseNumber("15");
                    setSharePointNetwork("");
                  }}
                >
                  Cancel
                </button>
                <button type="button" className="btn" style={{ background: "#8b1634", color: "#fff", fontWeight: 600 }} onClick={handleSharePointUpload}>
                  Upload to SharePoint
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditModal && editForm && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={{ maxWidth: 980, margin: "0 auto" }}>
              <div className="modal-header" style={{ background: THEME, color: "#fff" }}>
                <h5 className="modal-title">Edit SF-003-B Witness Statement</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowEditModal(false)} />
              </div>
              <div className="modal-body" style={{ maxHeight: "75vh", overflowY: "auto", background: "#f8fafc", padding: 16 }}>
                <div style={{ marginBottom: 14, border: "1px solid #cbd5e1", borderRadius: 10, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                  <div style={{ ...FIELD_STYLE, fontWeight: 700, background: "#efe2e7", color: THEME }}>Details of person giving the statement</div>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}><tbody>
                    <tr><td style={LABEL_STYLE}>Name</td><td style={FIELD_STYLE}><input className="form-control form-control-sm" value={editForm.name || ""} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></td></tr>
                    <tr><td style={LABEL_STYLE}>Job Title</td><td style={FIELD_STYLE}><input className="form-control form-control-sm" value={editForm.jobTitle || ""} onChange={(e) => setEditForm({ ...editForm, jobTitle: e.target.value })} /></td></tr>
                    <tr><td style={LABEL_STYLE}>Home Address</td><td style={FIELD_STYLE}><textarea className="form-control form-control-sm" rows={3} value={editForm.homeAddress || ""} onChange={(e) => setEditForm({ ...editForm, homeAddress: e.target.value })} /></td></tr>
                    <tr><td style={LABEL_STYLE}>Contact Telephone Number</td><td style={FIELD_STYLE}><input className="form-control form-control-sm" value={editForm.contactTelephoneNumber || ""} onChange={(e) => setEditForm({ ...editForm, contactTelephoneNumber: e.target.value.replace(/[^0-9]/g, "").slice(0, 12) })} maxLength={12} inputMode="numeric" pattern="[0-9]*" /></td></tr>
                  </tbody></table>
                </div>

                <div style={{ marginBottom: 14, border: "1px solid #cbd5e1", borderRadius: 10, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                  <div style={{ ...FIELD_STYLE, fontWeight: 700, background: "#efe2e7", color: THEME }}>Details of the Incident</div>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}><tbody>
                    <tr><td style={LABEL_STYLE}>Date of Incident</td><td style={FIELD_STYLE}><DatePicker selected={parseStoredDate(editForm.incidentDate)} onChange={(d) => setEditForm({ ...editForm, incidentDate: d ? dateToYmd(d) : "" })} dateFormat="dd-MM-yyyy" className="form-control form-control-sm" placeholderText="Select date" /></td></tr>
                    <tr><td style={LABEL_STYLE}>Time of Incident</td><td style={FIELD_STYLE}><input className="form-control form-control-sm" value={editForm.incidentTime || ""} onChange={(e) => setEditForm({ ...editForm, incidentTime: e.target.value })} /></td></tr>
                    <tr><td style={LABEL_STYLE}>Site Address / Location</td><td style={FIELD_STYLE}><input className="form-control form-control-sm" value={editForm.siteAddressLocation || ""} onChange={(e) => setEditForm({ ...editForm, siteAddressLocation: e.target.value })} /></td></tr>
                    <tr><td style={LABEL_STYLE}>Project Number</td><td style={FIELD_STYLE}><input className="form-control form-control-sm" value={editForm.projectNumber || ""} onChange={(e) => setEditForm({ ...editForm, projectNumber: e.target.value })} /></td></tr>
                    <tr><td style={LABEL_STYLE}>Exact Location of the Incident</td><td style={FIELD_STYLE}><input className="form-control form-control-sm" value={editForm.exactLocationOfIncident || ""} onChange={(e) => setEditForm({ ...editForm, exactLocationOfIncident: e.target.value })} /></td></tr>
                  </tbody></table>
                </div>

                <div style={{ marginBottom: 14, border: "1px solid #cbd5e1", borderRadius: 10, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                  <div style={{ ...FIELD_STYLE, fontWeight: 700, background: "#efe2e7", color: THEME }}>What Did You See? (Please give as much detail as you can)</div>
                  <div style={{ ...FIELD_STYLE }}><textarea className="form-control form-control-sm" rows={7} value={editForm.whatDidYouSee || ""} onChange={(e) => setEditForm({ ...editForm, whatDidYouSee: e.target.value })} /></div>
                </div>

                <div style={{ marginBottom: 8, border: "1px solid #cbd5e1", borderRadius: 10, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                  <div style={{ ...FIELD_STYLE, fontWeight: 600, color: THEME, background: "#f8f1f4" }}>I certify that the details completed within this document are correct to the best of my knowledge.</div>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}><tbody>
                    <tr>
                      <td style={LABEL_STYLE}>Signature</td>
                      <td style={FIELD_STYLE}>
                        <div style={{ width: "100%", border: "2px solid #8b1634", borderRadius: "18px", background: "#e9cfd6", padding: "0.5rem", textAlign: "center", minHeight: "120px", display: "flex", justifyContent: "center", alignItems: "center" }}>
                          {editForm.declarationSignature ? <img src={normalizeImageSource(editForm.declarationSignature, "image/png")} alt="Signature" style={{ maxWidth: "100%", maxHeight: "100px", objectFit: "contain", border: "1px solid #ddd", borderRadius: "8px", padding: "4px", backgroundColor: "white" }} /> : <div style={{ color: "#888", fontSize: "14px", padding: "20px" }}>No signature</div>}
                        </div>
                      </td>
                    </tr>
                    <tr><td style={LABEL_STYLE}>Date</td><td style={FIELD_STYLE}><DatePicker selected={parseStoredDate(editForm.declarationDate)} onChange={(d) => setEditForm({ ...editForm, declarationDate: d ? dateToYmd(d) : "" })} dateFormat="dd-MM-yyyy" className="form-control form-control-sm" placeholderText="Select date" /></td></tr>
                  </tbody></table>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
                <button type="button" className="btn" style={{ background: THEME, color: "#fff", fontWeight: 600 }} onClick={handleEditSave}>Submit</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AdminTaylorImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Upload SF-003-B Witness Statement File"
        importPath="witness-statement/import-form"
        isAdminUser={isAdminUser}
        onImported={({ insertedRow }) => {
          if (insertedRow && insertedRow._id) {
            setRecords((prev) => [insertedRow, ...prev]);
            setPage(1);
          }
        }}
      />

      {showDeleteModal && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete SF-003-B Witness Statement</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)} />
              </div>
              <div className="modal-body">Are you sure you want to delete this record?</div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                <button type="button" className="btn btn-danger" onClick={confirmDelete}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
